import {
	TableContainer,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Button,
	Divider,
	Grid,
	Typography,
	Popover,
	Paper,
	Box,
	Checkbox,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { AutoSizer, List } from 'react-virtualized';
import { SearchBar } from '../../atomicComponents';
import FilterMenu from '../FilterMenu/FilterMenu';
import ColumnMenu from '../ColumnMenu/ColumnMenu';
import { pxToRem, responsiveFontSizes } from '../../utils/getFontValue';
import TableHead from '../TableHead/TableHead';
import styles from './Table.module.scss';
import { connect } from 'react-redux';
import {
	updateTable,
	updateSelectedColumns,
} from '../../redux/actionCreators/tableFilter';
import { DARK_MODE } from '../../constants';
import { FilterIcon, ColumnsIcon, ExportIcon } from '../../assets/icons';
import * as XLSX from 'xlsx';

const CssButton = styled(Button)(({ themeMode }) => ({
	backgroundColor: themeMode === DARK_MODE ? '#fff' : '#000',
	color: themeMode === DARK_MODE ? '#000' : '#fff',
	padding: '0.3rem 0.5rem',
	'&:hover': {
		backgroundColor: themeMode === DARK_MODE ? '#fff' : '#000',
	},
	fontSize: pxToRem(10),
	fontWeight: '500',
	...responsiveFontSizes({ sm: 10, md: 12, lg: 14 }),
}));

const BaseButton = styled(Button)({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	justifyContent: 'center',
	alignItems: 'center',
	alignContent: 'center',
	padding: '12px 24px',
	gap: '16px',
	width: '223.51px',
	height: '56px',
	boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.16)',
	borderRadius: '12px',
	color: '#FFFFFF',
	fontFamily: "'JLR Emeric', sans-serif",
	fontStyle: 'normal',
	fontWeight: 400,
	fontSize: '20px',
	lineHeight: '25px',
	textTransform: 'none',
	whiteSpace: 'nowrap',
	border: 'none',
	cursor: 'pointer',
	transition: 'opacity 0.2s ease',
	'&:hover': {
		opacity: 0.9,
	},
	'&:active': {
		opacity: 0.8,
	},
});

const FilterButton = styled(BaseButton)({
	background: '#17201C',
});

const ColumnsButton = styled(BaseButton)({
	background: '#7B6C85',
});

const ExportButton = styled(BaseButton)({
	background: 'linear-gradient(90deg, #435C42 0%, #619C31 100%)',
});

const ViewReportButton = styled(BaseButton)({
	background: '#2F7DFF',
});

const classNames = {
	rowCount: {
		fontFamily: 'Uni Neue-Trial',
		fontWeight: 400,
		fontSize: '18px',
		lineHeight: '24px',
		color: '#242424',
		display: 'flex',
		alignItems: 'center',
		marginRight: '8px',
	},
};

const mapStateToProps = (state) => {
	return {
		table: state.table,
		themeMode: state.theme.themeMode,
	};
};

const mapDispatchToProps = (dispatch) => ({
	updateTable: (table) => dispatch(updateTable(table)),
	updateSelectedColumns: (listId, columnTitles) =>
		dispatch(updateSelectedColumns(listId, columnTitles)),
});

const BasicTable = ({
	listId = 'list',
	showCheckBox,
	showRowCount = false,
	search = null,
	columns: originalColumns,
	defaultColumns: originalDefaultColumns,
	rows: data,
	title,
	subtitle,
	rowHeight = 52,
	tableStyles = {},
	setHasActiveEdits,
	setCount,
	children,
	table,
	updateTable,
	updateSelectedColumns,
	themeMode,
	customiseTable,
	openReportModal,
	actionsConfig = {
		showFilter: false,
		showColumns: false,
		showExport: false,
		viewReport: false,
	},
}) => {
	const [rowsData, setRowsData] = useState({
		data: [...data],
		filteredData: [...data],
	});

	const vListRef = useRef(null);
	const tableHeadRef = useRef(null);

	const [ellipsisAnchorEl, setEllipsisAnchorEl] = useState(null);
	const [isEllipsisPopupOpen, setIsEllipsisPopupOpen] = useState(false);
	const [ellipsisPopupValue, setEllipsisPopupValue] = useState(null);
	const [showCopyAlert, setShowCopyAlert] = useState(false);

	const [fieldToFiltersSetMap, setFieldToFiltersSetMap] = useState({});
	const [searchQuery, setSearchQuery] = useState('');

	const filterOptionsFunc = useCallback(
		(fieldToSearch, query) => {
			for (const [field, filtersSet] of Object.entries(fieldToFiltersSetMap)) {
				if (filtersSet?.size > 0) {
					const matchingItems = rowsData.data.filter((res) =>
						filtersSet.has(res[field]),
					);

					const filteredData = matchingItems.filter((res) =>
						res[fieldToSearch]?.toLowerCase().includes(query.toLowerCase()),
					);

					return filteredData;
				}
			}
			const filteredData = rowsData.data.filter((res) =>
				res[fieldToSearch]?.toLowerCase().includes(query.toLowerCase()),
			);

			return filteredData;
		},
		[fieldToFiltersSetMap, rowsData.data],
	);

	const unsortedRows = useMemo(() => [...data], [data]);
	const hasLotNumber = useMemo(
		() =>
			data.some((row) => {
				const lotNumber = row?.lotNumber;
				return (
					lotNumber !== null &&
					lotNumber !== undefined &&
					String(lotNumber).trim() !== ''
				);
			}),
		[data],
	);
	const filteredOriginalColumns = useMemo(
		() =>
			originalColumns.filter(
				(col) => col.field !== 'lotNumber' || hasLotNumber,
			),
		[originalColumns, hasLotNumber],
	);
	const filteredOriginalDefaultColumns = useMemo(
		() =>
			originalDefaultColumns.filter(
				(col) => col.field !== 'lotNumber' || hasLotNumber,
			),
		[originalDefaultColumns, hasLotNumber],
	);

	const handlePopoverOpen = (event, val) => {
		if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth) {
			setEllipsisPopupValue(val);
			setIsEllipsisPopupOpen(true);
			setEllipsisAnchorEl(event.currentTarget);
		}
	};

	const handlePopoverClose = useCallback(() => {
		setIsEllipsisPopupOpen(false);
		setEllipsisAnchorEl(null);
	}, []);

	const onCopy = useCallback((text) => {
		navigator.clipboard.writeText(text);
		setShowCopyAlert(true);
	}, []);

	const closeEllipsisPopup = useCallback(
		() => setIsEllipsisPopupOpen(false),
		[],
	);

	const handleRowSelection = useCallback((selectedId) => {
		setRowsData((prev) => ({
			...prev,
			data: prev.data.map((row) => {
				return row.id === selectedId
					? { ...row, isSelected: !row.isSelected }
					: row;
			}),
			filteredData: prev.filteredData.map((row) => {
				return row.id === selectedId
					? { ...row, isSelected: !row.isSelected }
					: row;
			}),
		}));
	}, []);

	const defaultColumns = useMemo(
		() =>
			[
				showCheckBox && {
					width: 50,
					render: ({ isSelected, id }) => {
						return (
							<Checkbox
								sx={{
									margin: 0,
									padding: 0,
									color: '#707070',
									'&.Mui-checked': {
										color: '#FFA222',
									},
								}}
								onChange={(e) => handleRowSelection(e.target.checked)}
								checked={!!isSelected}
								inputProps={{
									'data-testid': `select-row-${id}`,
								}}
							/>
						);
					},
				},
				...filteredOriginalDefaultColumns,
			].filter((i) => i),
		[showCheckBox, filteredOriginalDefaultColumns, handleRowSelection],
	);

	const [localSelectedColumns, setLocalSelectedColumns] = useState(() => {
		const storedTitles = table.selectedColumns?.[listId];

		if (storedTitles && storedTitles.length > 0) {
			const filteredColumns = [
				...defaultColumns.slice(
					0,
					defaultColumns.length - filteredOriginalDefaultColumns.length,
				),
				...filteredOriginalColumns.filter((col) => {
					const title = col.title || col.label || col.field;
					return storedTitles.includes(title);
				}),
			];
			return filteredColumns;
		}
		return defaultColumns;
	});

	const updateSelectedColumnsState = useCallback(
		(newColumns) => {
			setLocalSelectedColumns(newColumns);

			const columnTitles = newColumns
				.filter((col) => {
					const title = col.title || col.label || col.field;
					return title !== 'select' && title !== '#';
				})
				.map((col) => col.title || col.label || col.field);

			updateSelectedColumns(listId, columnTitles);
		},
		[listId, updateSelectedColumns],
	);

	const customizeTableButtonRef = useRef(null);
	const filterButtonRef = useRef(null);
	const columnButtonRef = useRef(null);
	const [fieldToSearchTextMap, setFieldToSearchTextMap] = useState({});
	const [isCustomizeTableMenuOpen, setIsCustomizeTableMenuOpen] =
		useState(false);
	const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
	const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
	const [filters, setFilters] = useState({});

	// Handle filter changes
	const handleFilterChange = useCallback(
		(newFilters) => {
			setFilters(newFilters);

			// Convert filters to the format expected by the existing system
			const fieldToFiltersSetMap = {};
			Object.entries(newFilters).forEach(([field, values]) => {
				if (values && values.length > 0) {
					fieldToFiltersSetMap[field] = new Set(values);
				}
			});

			// Update the existing filter system
			updateTable({
				...table,
				filter: {
					[listId]: fieldToFiltersSetMap,
				},
			});

			setFieldToFiltersSetMap(fieldToFiltersSetMap);
		},
		[table, listId, updateTable],
	);

	// Handle column changes
	const handleColumnChange = useCallback(
		(selectedColumns) => {
			const defaultCols = defaultColumns.slice(
				0,
				defaultColumns.length - filteredOriginalDefaultColumns.length,
			);

			// Then, get the selected original columns
			const selectedOriginalCols = filteredOriginalColumns.filter((col) => {
				const title = col.title || col.label || col.field;
				return selectedColumns.includes(title);
			});

			// Combine them
			const newColumns = [...defaultCols, ...selectedOriginalCols];

			updateSelectedColumnsState(newColumns);
		},
		[
			filteredOriginalColumns,
			defaultColumns,
			filteredOriginalDefaultColumns,
			updateSelectedColumnsState,
		],
	);

	function filterBySearchTexts(fieldToSearchTextMap, data, columns) {
		const fieldToSearchFnMap = columns.reduce((acc, col) => {
			if (col.searchFn) return { ...acc, [col.field]: col.searchFn };
			return acc;
		}, {});

		let filteredData = [...data];
		Object.entries(fieldToSearchTextMap).forEach((entry) => {
			const [field, searchText] = entry;
			const defaultSearchFn = (fieldValue, searchValue) =>
				fieldValue.toLowerCase().startsWith(searchValue);

			const searchFn = fieldToSearchFnMap[field] || defaultSearchFn;

			filteredData = filteredData.filter(
				(row) => !searchText || !row[field] || searchFn(row[field], searchText),
			);
		});
		return filteredData;
	}

	const handleColumnSelectionChange = useCallback(
		(columnTitle) => {
			if (localSelectedColumns.map(({ title }) => title).includes(columnTitle))
				return updateSelectedColumnsState(
					localSelectedColumns.filter((column) => column.title !== columnTitle),
				);

			return updateSelectedColumnsState([
				...localSelectedColumns,
				...filteredOriginalColumns.filter(({ title }) => title === columnTitle),
			]);
		},
		[localSelectedColumns, filteredOriginalColumns, updateSelectedColumnsState],
	);

	const applyColumnsSelections = useCallback(
		(columnSelections) => {
			updateSelectedColumnsState([
				...defaultColumns.slice(
					0,
					defaultColumns.length - filteredOriginalDefaultColumns.length,
				),
				...filteredOriginalColumns.filter((col) =>
					columnSelections.includes(col.title),
				),
			]);
		},
		[
			defaultColumns,
			filteredOriginalDefaultColumns,
			filteredOriginalColumns,
			updateSelectedColumnsState,
		],
	);

	// When the virtualized list scrolls horizontally, keep the header in sync
	const onListScroll = useCallback(({ scrollLeft }) => {
		if (tableHeadRef.current && typeof scrollLeft === 'number') {
			tableHeadRef.current.scrollLeft = scrollLeft;
		}
	}, []);

	function filterBySelectedFilters(fieldToFiltersSetMap, data, columns) {
		const fieldToFilterFnMap = columns.reduce((acc, col) => {
			if (col.filterFn) return { ...acc, [col.field]: col.filterFn };
			return acc;
		}, {});

		let filteredData = [...data];

		Object.entries(fieldToFiltersSetMap).forEach(([field, filtersSet]) => {
			const filterFn = fieldToFilterFnMap[field];

			if (filtersSet?.size === 0) {
				return;
			}

			filteredData = filteredData.filter((row) => {
				if (!filterFn) {
					return filtersSet.has(row[field]);
				}

				return filtersSet.some((filter) => filterFn(row, filter.value));
			});
		});
		return filteredData;
	}

	// Effect for search functionality
// Updated search effect
useEffect(() => {
    setRowsData((prevState) => {
        const filteredOptions = searchQuery && searchQuery !== '' && search
            ? filterOptionsFunc(search?.fieldToSearch, searchQuery)
            : [...prevState.data];
        return { ...prevState, filteredData: filteredOptions };
    });
    // No pagination, so no need to reset page
}, [searchQuery, search, filterOptionsFunc]);


// Updated filtering & column effect
useEffect(() => {
    setRowsData((prevState) => {
        const dataFilteredBySearchTexts = filterBySearchTexts(
            fieldToSearchTextMap,
            prevState.data,
            localSelectedColumns,
        );

        const dataFilteredBySearchAndFilters = filterBySelectedFilters(
            (table.filter && table?.filter[listId]) ?? fieldToFiltersSetMap,
            dataFilteredBySearchTexts,
            localSelectedColumns,
        );
        return { ...prevState, filteredData: dataFilteredBySearchAndFilters };
    });
    // No pagination, so no need to reset page
}, [
    localSelectedColumns,
    table,
    fieldToSearchTextMap,
    listId,
    fieldToFiltersSetMap,
]);
	// Note: We rely on List's onScroll to drive header sync. No DOM listeners needed.

	useEffect(() => {
		if (showCopyAlert) {
			const timer = setTimeout(() => setShowCopyAlert(false), 2000);
			return () => clearTimeout(timer);
		}
	}, [showCopyAlert]);

	// Effect for data updates
	useEffect(() => {
    setRowsData({
        data: [...data],
        filteredData: [...data],
    });
    // No pagination, so no need to reset page
}, [data]);

	// Effect for edit state tracking
	useEffect(() => {
		if (setHasActiveEdits) {
			const areEditsActive = rowsData.data.some((row) => row.isSelected);
			setHasActiveEdits(areEditsActive);
		}
	}, [rowsData.data, setHasActiveEdits]);

	// Effect for count tracking
	useEffect(() => {
		if (setCount) {
			setCount(rowsData.filteredData.length);
		}
	}, [rowsData.filteredData, setCount]);

	// Use all filtered data for virtual scrolling instead of pagination
	const virtualRows = useMemo(() => {
		return rowsData.filteredData;
	}, [rowsData.filteredData]);

	const rowRenderer = useCallback(
		({ style, index, key }) => {
			const row = virtualRows[index];
			if (!row) return null;

			const isRowSelected = row?.isSelected;

			const getColumnStyles = (column) => {
				if (column.columnStyle && typeof column.columnStyle === 'function')
					return { ...column.columnStyle(row) };
				if (column.columnStyle && typeof column.columnStyle === 'object')
					return { ...column.columnStyle };
				return {};
			};

			return (
				<TableRow
					component="div"
					index={index}
					style={style}
					key={key}
					className={styles.tableRow}
					sx={{
						display: 'flex',
						alignItems: 'center',
						minWidth: '100%',
						width: 'auto !important',
						backgroundColor: isRowSelected && showCheckBox && '#1B681B',
						color: themeMode === DARK_MODE ? '#FFFFFF' : 'inherit',
						'&:hover': {
							backgroundColor:
								isRowSelected && showCheckBox
									? '#1B681B'
									: themeMode === DARK_MODE
									? 'rgba(255, 255, 255, 0.04)'
									: 'rgba(0, 0, 0, 0.04)',
						},
					}}
				>
					{localSelectedColumns?.map((column) => {
						const renderColumnCell = () =>
							column.render && row ? (
								column.render(
									row,
									{
										openEllipsisPopupForEventWithText: handlePopoverOpen,
										closeEllipsisPopup: handlePopoverClose,
										onCopy,
									},
									index,
								)
							) : (
								<Box
									sx={{
										letterSpacing: 0.5,
										fontSize: pxToRem(15),
										...responsiveFontSizes({ sm: 15, md: 17, lg: 21 }),
										...getColumnStyles(column),
									}}
								>
									<Typography
										sx={{
											fontWeight: isRowSelected ? '700' : '800',
											color: themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
											opacity:
												themeMode === DARK_MODE || isRowSelected
													? '100%'
													: '100%',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											fontSize: pxToRem(15),
											...responsiveFontSizes({ sm: 14, md: 15, lg: 21 }),
											...getColumnStyles(column),
										}}
										onMouseEnter={(e) =>
											handlePopoverOpen(e, row[column?.field])
										}
										onMouseLeave={() => handlePopoverClose()}
										onDoubleClick={() => onCopy(row[column.field])}
									>
										{row[column.field] ? row[column.field] : '-'}
									</Typography>
								</Box>
							);
						return (
							<TableCell
								component="div"
								className={styles.tableCell}
								sx={{
									paddingTop: 0.25,
									paddingBottom: 0.25,
									minWidth: column.width,
									maxWidth: column.width,
									color: themeMode === DARK_MODE ? '#FFFFFF' : 'inherit',
								}}
								key={`${key}-${column.field}`}
							>
								{renderColumnCell()}
							</TableCell>
						);
					})}
				</TableRow>
			);
		},
		[
			virtualRows,
			showCheckBox,
			themeMode,
			localSelectedColumns,
			handlePopoverOpen,
			handlePopoverClose,
			onCopy,
		],
	);

	const reorder = useCallback((list, startIndex, endIndex) => {
		const result = [...list];
		const [removed] = result.splice(startIndex, 1);
		result.splice(endIndex, 0, removed);
		return result;
	}, []);

	const noOfFixedColumns = 1;

	const reorderHeaders = useCallback(
		(result) => {
			if (!result.destination || result.destination.index < noOfFixedColumns) {
				return;
			}

			const reorderedColumns = reorder(
				localSelectedColumns,
				result.source.index,
				result.destination.index,
			);
			updateSelectedColumnsState(reorderedColumns);
		},
		[localSelectedColumns, reorder, updateSelectedColumnsState],
	);

	const clearAllFilters = useCallback(() => {
		updateTable({ filter: { [listId]: {} } });
		setFieldToFiltersSetMap({});
		setFilters({});
		// No pagination, so no need to reset page
	}, [updateTable, listId]);

	const hasNonEmptySet = useMemo(
		() =>
			table?.filter?.[listId] &&
			Object.values(table.filter[listId]).some(
				(filterSet) => filterSet instanceof Set && filterSet.size > 0,
			),
		[table?.filter, listId],
	);

	const selectedRows = useMemo(
		() => rowsData.data.filter((row) => row.isSelected),
		[rowsData.data],
	);

	const handleExport = () => {
		const exportData = rowsData.filteredData.map((row) => {
			const newRow = {};
			filteredOriginalColumns
				.filter((c) => localSelectedColumns.includes(c.key))
				.forEach((c) => {
					newRow[c.label || c.key] = row[c.key] ?? '';
				});

			return newRow;
		});

		const worksheet = XLSX.utils.json_to_sheet(exportData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

		XLSX.writeFile(workbook, 'Kspec Download.xlsx');
	};

	return (
		<>
			{title && (
				<Grid
					container
					spacing={2}
					alignItems="center"
					justifyContent="space-between"
					sx={{ padding: 2 }}
				>
					{/* Left: Row count + Title */}
					<Grid item xs={12} md={6} container alignItems="center" spacing={1}>
						{showRowCount && (
							<Typography
								variant="subtitle1"
								component="span"
								letterSpacing={0.5}
								sx={classNames.rowCount}
							>
								{rowsData.filteredData.length}
							</Typography>
						)}
						<Typography
							variant="h4"
							letterSpacing={0.5}
							sx={{ fontWeight: 600 }}
						>
							{title}
						</Typography>
					</Grid>

					{/* Right: Search + Buttons */}
					<Grid item xs={12} md={6}>
						<Grid
							container
							spacing={1}
							alignItems="center"
							justifyContent="flex-end"
							flexWrap="wrap"
						>
							{/* Search bar */}
							{search && (
								<Grid
									item
									xs={12}
									sm="auto"
									sx={{ flexGrow: 1, minWidth: '200px', maxWidth: '300px' }}
								>
									<SearchBar
										value={searchQuery}
										onChange={setSearchQuery}
										placeholder={search?.placeholder}
									/>
								</Grid>
							)}

							{/* Buttons */}
							<Grid
								item
								xs={12}
								sm="auto"
								className={styles.actionsButtonContainer}
								sx={{
									display: 'flex',
									gap: 1,
									flexWrap: 'wrap',
									justifyContent: { xs: 'flex-start', md: 'flex-end' },
									marginTop: { xs: 1, sm: 0 },
								}}
							>
								{hasNonEmptySet && (
									<CssButton
										themeMode={themeMode}
										size="small"
										onClick={clearAllFilters}
									>
										Clear All Filters
									</CssButton>
								)}

								{actionsConfig?.showFilter && (
									<FilterButton
										onClick={() => setIsFilterMenuOpen(true)}
										ref={filterButtonRef}
										className={styles.filterButton}
									>
										<FilterIcon />
										Filter
									</FilterButton>
								)}

								{actionsConfig?.showColumns && (
									<ColumnsButton
										onClick={() => setIsColumnMenuOpen(true)}
										ref={columnButtonRef}
										className={styles.columnsButton}
									>
										<ColumnsIcon />
										Columns
									</ColumnsButton>
								)}

								{actionsConfig?.showExport && (
									<ExportButton
										onClick={handleExport}
										className={styles.exportButton}
									>
										<ExportIcon />
										Export
									</ExportButton>
								)}

								{actionsConfig?.viewReport && (
									<ViewReportButton
										onClick={openReportModal}
										className={styles.viewButton}
									>
										<ExportIcon />
										View Report
									</ViewReportButton>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			)}

			{subtitle && (
				<Box
					sx={{
						position: 'relative',
						width: '100%',
						height: '56px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						marginBottom: '10px',
						background: 'transparent',
						borderRadius: '24px 24px 0 0',
						padding: '0 20px',
					}}
				>
					<Box
						sx={{
							position: 'absolute',
							left: '20px',
							top: '6px',
							width: '220px',
							height: '44px',
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
						}}
					>
						{showRowCount && (
							<Typography
								variant="subtitle1"
								component="span"
								letterSpacing={0.5}
								sx={{
									fontFamily: 'Uni Neue-Trial',
									fontWeight: 400,
									fontSize: '56px',
									lineHeight: '24px',
									color: (theme) =>
										theme.palette.mode === 'dark' ? '#FFFFFF' : '#242424',
									display: 'flex',
									alignItems: 'center',
								}}
							>
								{rowsData.filteredData.length}
							</Typography>
						)}
						<Typography
							sx={{
								fontFamily: "'JLR Emeric', sans-serif",
								fontStyle: 'normal',
								fontWeight: 600,
								fontSize: '56px',
								lineHeight: '44px',
								color: (theme) =>
									theme.palette.mode === 'dark' ? '#FFFFFF' : '#202020',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							{subtitle}
						</Typography>
					</Box>

					{(search ||
						hasNonEmptySet ||
						actionsConfig?.showFilter ||
						actionsConfig?.showColumns ||
						actionsConfig?.showExport) && (
						<Box
							sx={{
								position: 'absolute',
								right: '20px',
								top: '0px',
								width: '531px',
								height: '56px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-end',
								gap: '10px',
							}}
						>
							{search && (
								<Box sx={{ flexGrow: 1, maxWidth: '300px' }}>
									<SearchBar
										value={searchQuery}
										onChange={setSearchQuery}
										placeholder={search?.placeholder}
									/>
								</Box>
							)}

							{hasNonEmptySet && (
								<CssButton
									themeMode={themeMode}
									size="small"
									onClick={clearAllFilters}
								>
									Clear All Filters
								</CssButton>
							)}

							{actionsConfig?.showFilter && (
								<FilterButton
									onClick={() => setIsFilterMenuOpen(true)}
									ref={filterButtonRef}
									className={styles.filterButton}
								>
									<FilterIcon />
									Filter
								</FilterButton>
							)}
							{actionsConfig?.showColumns && (
								<ColumnsButton
									onClick={() => setIsColumnMenuOpen(true)}
									ref={columnButtonRef}
									className={styles.columnsButton}
								>
									<ColumnsIcon />
									Columns
								</ColumnsButton>
							)}
							{actionsConfig?.showExport && (
								<ExportButton
									onClick={handleExport}
									className={styles.exportButton}
								>
									<ExportIcon />
									Export
								</ExportButton>
							)}
						</Box>
					)}
				</Box>
			)}

			<Paper
				className={styles.tableContainer}
				sx={{
					...tableStyles.paper,
					border:
						themeMode === DARK_MODE ? '1px solid #595959' : '1px solid #B0B0B0',
					boxShadow:
						themeMode === DARK_MODE
							? '0px 0px 4px rgba(0, 0, 0, 0.25)'
							: '0px 0px 4px rgba(0, 0, 0, 0.25)',
					borderRadius: subtitle ? '0 0 24px 24px' : '24px',
					elevation: 1,
					backgroundColor: themeMode === DARK_MODE ? '#151A1E' : '#FFFFFF',
				}}
				elevation={1}
			>
				<TableContainer
					sx={{
						position: 'relative',
						width: '100%',
						height: (() => {
							const headerHeight = 48;
							const rowHeight = 52;
							// Use the actual tableStyles.tableContainer height if provided, otherwise calculate based on data
							if (tableStyles.tableContainer?.height) {
								return tableStyles.tableContainer.height;
							}
							// Show all rows with a maximum height to enable scrolling
							const maxVisibleRows = 8; // Show up to 8 rows before scrolling
							const actualRows = Math.min(virtualRows.length, maxVisibleRows);
							return `${
								headerHeight + actualRows * rowHeight
							}px`;
						})(),
						background: themeMode === DARK_MODE ? '#151A1E' : '#F8F8F8',
						padding: 0,
						overflow: 'hidden',
						...tableStyles.tableContainer,
					}}
					component={Box}
				>
					<Table
						component="div"
						sx={{
							height: '100%',
							width: '100%',
							overflowX: 'auto',
							overflowY: 'auto', // Allow vertical scrolling
						}}
					>
						<AutoSizer>
							{({ width: windowWidth, height: windowHeight }) => {
								const width = windowWidth;
								const height = windowHeight;
								// Calculate list height based on all virtual rows, not limited to 10
								const listHeight = virtualRows.length * rowHeight;

								return (
									<div style={{ height, width }}>
										<TableHead
											updateTable={updateTable}
											table={table}
											ref={tableHeadRef}
											rows={rowsData.filteredData}
											columns={localSelectedColumns}
											allRows={rowsData.data}
											fieldToFiltersSetMap={
												(table?.filter && table?.filter[listId]) ??
												fieldToFiltersSetMap
											}
											setSortedRows={(sortedRows) => {
												setRowsData({
													data: sortedRows,
													filteredData: sortedRows,
												});
											}}
											unsortedRows={unsortedRows}
											setFiltersForFieldCurry={(field) => (selectedFilters) => {
												updateTable({
													...table,
													filter: {
														[listId]: {
															...table?.filter[listId],
															[field]: new Set(selectedFilters),
														},
													},
												});
												setFieldToFiltersSetMap((prev) => ({
													...prev,
													[field]: new Set(selectedFilters),
												}));
											}}
											fieldToSearchTextMap={fieldToSearchTextMap}
											setSearchTextForTitleCurry={(field) => (value) =>
												setFieldToSearchTextMap((prev) => ({
													...prev,
													[field]: value.toLowerCase(),
												}))}
											reorderHeaders={reorderHeaders}
											listId={listId}
										/>
										<TableBody component="div">
											{virtualRows.length === 0 ? (
												<Box
													sx={{
														display: 'flex',
														justifyContent: 'center',
														alignItems: 'center',
														height: '100px',
														width: '100%',
														fontFamily: 'Uni Neue-Trial',
														fontSize: '18px',
														fontWeight: 900,
														color:
															themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
													}}
												>
													No data available
												</Box>
											) : (
												<List
													ref={vListRef}
													id={listId}
													containerStyle={{
														overflowX: 'auto',
														overflowY: 'auto', // Allow vertical scrolling
														height: height - 48, // Subtract header height
														width: width,
													}}
													rowRenderer={rowRenderer}
													height={height - 48} // Use available height minus header
													rowHeight={rowHeight}
													rowCount={virtualRows.length}
													width={width}
													className={clsx(
														styles.scrollBar,
														styles.heightAdjustedListScroll,
													)}
													overscanRowCount={5} // Increase overscan for better performance
													scrollToAlignment="start"
													onScroll={onListScroll}
												/>
											)}

										</TableBody>
									</div>
								);
							}}
						</AutoSizer>
					</Table>
				</TableContainer>
				{showCheckBox && children && typeof children === 'function'
					? children(selectedRows)
					: children}
			</Paper>

			<FilterMenu
				anchorEl={filterButtonRef.current}
				open={isFilterMenuOpen}
				handleClose={() => setIsFilterMenuOpen(false)}
				columns={filteredOriginalColumns}
				rows={rowsData.data}
				filters={
					table?.filter?.[listId]
						? Object.fromEntries(
								Object.entries(table.filter[listId]).map(([key, valueSet]) => [
									key,
									valueSet instanceof Set ? Array.from(valueSet) : valueSet,
								]),
						  )
						: filters
				}
				onFilterChange={handleFilterChange}
			/>

			<ColumnMenu
				anchorEl={columnButtonRef.current}
				open={isColumnMenuOpen}
				handleClose={() => setIsColumnMenuOpen(false)}
				columns={filteredOriginalColumns.map(
					(col) => col.title || col.label || col.field,
				)}
				currentSelections={localSelectedColumns
					.filter((col) => {
						const title = col.title || col.label || col.field;
						return title !== 'select' && title !== '#';
					})
					.map((col) => col.title || col.label || col.field)}
				onColumnChange={handleColumnChange}
				onResetToDefault={() => updateSelectedColumnsState([...defaultColumns])}
				defaultColumns={filteredOriginalDefaultColumns.map(
					(col) => col.title || col.label || col.field,
				)}
			/>

			<Popover
				open={isEllipsisPopupOpen}
				anchorEl={ellipsisAnchorEl}
				onClose={closeEllipsisPopup}
				className={styles.popover}
				classes={{
					paper: styles.popoverPaper,
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
			>
				{ellipsisPopupValue}
			</Popover>

			<div
				className={clsx(
					styles.copyAlert,
					showCopyAlert ? styles.visible : styles.hidden,
				)}
			>
				Text copied
			</div>
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(BasicTable);
